import { createRouter, createWebHistory } from 'vue-router';
import { useDoctorStore } from '@/store/doctor';

// when user logged-in can't access to those paths
const redirectedPaths = ['login', 'register', 'reset'];

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/App.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/Home.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/patients',
        name: 'patients',
        component: () => import('@/views/Patients.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/activity',
        name: 'activity',
        component: () => import('@/views/Activity.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/queue',
        name: 'queue',
        component: () => import('@/views/Queue.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/patients/:id',
        name: 'patientDetails',
        component: () => import('@/views/PatientProfile.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        // The digital twin at full size. A child of the shell rather than a
        // top-level route, so it inherits the auth guard and the doctor keeps
        // the nav they came in with. The feature's only switch is still the
        // server's, so a pasted URL with DIGITAL_TWIN_PROTOTYPE off renders
        // nothing rather than a disabled feature.
        path: '/patients/:id/twin',
        name: 'patientDigitalTwin',
        component: () => import('@/views/TwinViewer.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/admin/patients/:id/raw-data',
        name: 'adminPatientRawData',
        component: () => import('@/views/admin/PatientRawData.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: '/admin/engagement',
        name: 'adminEngagement',
        component: () => import('@/views/admin/EngagementReport.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: '/compare',
        name: 'compare',
        component: () => import('@/views/ComparePatients.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/doctors',
        name: 'adminDoctors',
        component: () => import('@/views/Doctors.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: '/doctors/:id',
        name: 'adminDoctorProfile',
        component: () => import('@/views/DoctorProfile.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: '/admin/pending',
        name: 'adminPendingApprovals',
        component: () => import('@/views/PendingApprovals.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: '/profile',
        name: 'profile',
        component: () => import('@/views/Profile.vue'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
  },
  // The three auth views are self-contained: each owns its own page chrome
  // (DESIGN.md "Ink Panel" layout).
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
  },
  {
    path: '/reset',
    name: 'reset',
    component: () => import('@/views/ResetPassword.vue'),
  },
  // Shown to an authenticated doctor whose account is still on the
  // admin waiting list (role `waiting_list_doctor`). The guard below
  // decides who may land here; an approved doctor is sent home, a
  // logged-out visitor to /login.
  {
    path: '/pending',
    name: 'pending',
    component: () => import('@/views/PendingVerification.vue'),
  },
  // Wearable OAuth callback - where the vendor redirect lands with
  // ?code= and &state= after a patient's ring is authorized. Deliberately
  // public and explicitly registered: a guarded route would hard-logout an
  // unauthenticated visitor and destroy the single-use code, and the
  // catch-all below is a string redirect, which drops the query string.
  // The view checks login state itself and says so instead.
  {
    path: '/wearables/:vendor/callback',
    name: 'wearableCallback',
    component: () => import('@/views/WearableCallback.vue'),
  },
  // A patient's private report link and the check for a printed report ID,
  // opened by doctors with no eSteps account. Public: the guard lets them
  // through without asking the server who is signed in, and they carry no
  // dashboard chrome.
  {
    path: '/r/:token',
    name: 'sharedReport',
    component: () => import('@/views/public/SharedReportGate.vue'),
    meta: { public: true },
  },
  {
    path: '/verify/:reportId',
    name: 'verifyReport',
    component: () => import('@/views/public/VerifyReport.vue'),
    meta: { public: true },
  },
  // Page not Found
  {
    path: '/404',
    name: 'notFound',
    component: () => import('@/views/PageNotFound.vue'),
  },
  // redirection 404 page
  {
    path: '/:catchAll(.*)',
    redirect: '/404',
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    next();
    return;
  }
  const doctor = useDoctorStore();
  const status = await doctor.getAuthStatus();

  // Pending-verification screen: only an authenticated doctor who is not
  // yet approved should see it. Approved doctors go home; logged-out
  // visitors go to login.
  if (to.name === 'pending') {
    if (status.approved) {
      next('/');
    } else if (status.authenticated) {
      next();
    } else {
      next('/login');
    }
    return;
  }

  if (to.meta.requiresAuth) {
    if (status.approved) {
      // Admin-only routes (e.g. /doctors) - a logged-in doctor without
      // the admin role gets bounced to home rather than 404'd, so the
      // URL itself isn't leaked as a hint about an admin surface.
      if (to.meta.requiresAdmin && !status.isAdmin) {
        next('/');
        return;
      }
      next();
    } else if (status.authenticated) {
      // Authenticated but still on the waiting list - surface the
      // pending screen instead of bouncing to /login with no context.
      next({ name: 'pending' });
    } else {
      // if the sessions where delete from Parse cloud
      // You may have problem to login using the old token session
      await doctor.logout();
      next('/login');
    }
  } else if (status.approved && redirectedPaths.includes(to.name)) {
    next('/');
  } else {
    // if the sessions where delete from Parse cloud
    // You may have problem to login using the old token session
    next();
  }
});

router.afterEach((to) => {
  if (to.name === 'notFound') {
    document.title = '404 Not Found';
  }
});

export default router;
